import React, { useState } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Lock, KeyRound, Loader2, AtSign } from 'lucide-react';
import { toast } from 'sonner';
import { PasswordStrength, isPasswordValid } from '../components/PasswordStrength';

export default function Settings() {
  const { user, setUser } = useAuth();
  const [pinForm, setPinForm] = useState({ new_pin: '', confirm_pin: '' });
  const [savingPin, setSavingPin] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [savingPw, setSavingPw] = useState(false);
  const [emailForm, setEmailForm] = useState({ current_password: '', new_email: '' });
  const [savingEmail, setSavingEmail] = useState(false);

  const handleChangeEmail = async () => {
    if (!emailForm.current_password || !emailForm.new_email) {
      toast.error('Bitte Passwort und neue E-Mail eingeben');
      return;
    }
    setSavingEmail(true);
    try {
      const res = await api.put('/auth/change-email', {
        current_password: emailForm.current_password,
        new_email: emailForm.new_email.trim(),
      });
      toast.success('E-Mail-Adresse erfolgreich geändert');
      if (setUser && user) setUser({ ...user, email: res.data.email });
      setEmailForm({ current_password: '', new_email: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Ändern der E-Mail');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleChangePassword = async () => {
    if (!pwForm.current_password || !pwForm.new_password) {
      toast.error('Bitte aktuelles und neues Passwort eingeben');
      return;
    }
    if (pwForm.new_password.length < 8) {
      toast.error('Das neue Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }
    if (!isPasswordValid(pwForm.new_password)) {
      toast.error('Das Passwort erfüllt nicht alle Sicherheitsregeln');
      return;
    }
    if (pwForm.new_password !== pwForm.confirm_password) {
      toast.error('Die Passwörter stimmen nicht überein');
      return;
    }
    setSavingPw(true);
    try {
      await api.put('/auth/change-password', {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success('Passwort erfolgreich geändert');
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Ändern des Passworts');
    } finally {
      setSavingPw(false);
    }
  };

  const handleChangePin = async () => {
    if (!pinForm.new_pin || pinForm.new_pin.length !== 4 || !/^\d{4}$/.test(pinForm.new_pin)) {
      toast.error('PIN muss genau 4 Ziffern haben');
      return;
    }
    if (pinForm.new_pin !== pinForm.confirm_pin) {
      toast.error('PINs stimmen nicht überein');
      return;
    }
    setSavingPin(true);
    try {
      await api.put('/auth/change-pin', { new_pin: pinForm.new_pin });
      toast.success('PIN erfolgreich geändert');
      setPinForm({ new_pin: '', confirm_pin: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Ändern des PINs');
    } finally {
      setSavingPin(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="settings-page">
        <div>
          <h1 className="font-heading font-bold text-3xl text-foreground">Einstellungen</h1>
          <p className="text-muted-foreground mt-1">
            Verwalten Sie Ihre Kontoeinstellungen
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card className="border-border/60" data-testid="change-email-card">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <AtSign className="h-5 w-5 text-primary" />
                  E-Mail ändern
                </CardTitle>
                <CardDescription>
                  Ihre aktuelle Login-E-Mail: <strong>{user?.email}</strong>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new_email">Neue E-Mail-Adresse</Label>
                  <Input
                    id="new_email"
                    type="email"
                    placeholder="name@firma.de"
                    value={emailForm.new_email}
                    onChange={(e) => setEmailForm((p) => ({ ...p, new_email: e.target.value }))}
                    data-testid="new-email-input"
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email_current_password">Aktuelles Passwort (zur Bestätigung)</Label>
                  <Input
                    id="email_current_password"
                    type="password"
                    placeholder="••••••••"
                    value={emailForm.current_password}
                    onChange={(e) => setEmailForm((p) => ({ ...p, current_password: e.target.value }))}
                    data-testid="email-current-password"
                    autoComplete="current-password"
                  />
                </div>
                <Button
                  onClick={handleChangeEmail}
                  disabled={savingEmail || !emailForm.current_password || !emailForm.new_email}
                  className="rounded-full"
                  data-testid="change-email-btn"
                >
                  {savingEmail ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <AtSign className="mr-2 h-4 w-4" />}
                  E-Mail ändern
                </Button>
              </CardContent>
            </Card>

            <Card className="border-border/60">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Lock className="h-5 w-5 text-primary" />
                  Passwort ändern
                </CardTitle>
                <CardDescription>Aktualisieren Sie Ihr Passwort</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="current_password">Aktuelles Passwort</Label>
                  <Input
                    id="current_password"
                    type="password"
                    value={pwForm.current_password}
                    onChange={(e) => setPwForm((p) => ({ ...p, current_password: e.target.value }))}
                    data-testid="current-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new_password">Neues Passwort</Label>
                  <Input
                    id="new_password"
                    type="password"
                    value={pwForm.new_password}
                    onChange={(e) => setPwForm((p) => ({ ...p, new_password: e.target.value }))}
                    data-testid="new-password"
                  />
                </div>
                <PasswordStrength password={pwForm.new_password} />
                <div className="space-y-2">
                  <Label htmlFor="confirm_password">Passwort bestätigen</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    value={pwForm.confirm_password}
                    onChange={(e) => setPwForm((p) => ({ ...p, confirm_password: e.target.value }))}
                    data-testid="confirm-password"
                  />
                </div>
                {pwForm.new_password && pwForm.confirm_password && pwForm.new_password !== pwForm.confirm_password && (
                  <p className="text-sm text-destructive">Die Passwörter stimmen nicht überein</p>
                )}
                <Button
                  variant="outline"
                  className="rounded-full"
                  onClick={handleChangePassword}
                  disabled={savingPw || !pwForm.current_password || !pwForm.new_password || pwForm.new_password !== pwForm.confirm_password || !isPasswordValid(pwForm.new_password)}
                  data-testid="change-password-btn"
                >
                  {savingPw ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Passwort ändern
                </Button>
              </CardContent>
            </Card>

            <Card className="border-border/60">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <KeyRound className="h-5 w-5 text-primary" />
                  Zeiterfassungs-PIN aendern
                </CardTitle>
                <CardDescription>Aendern Sie Ihren 4-stelligen PIN fuer die Zeiterfassung</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new_pin">Neuer PIN (4 Ziffern)</Label>
                  <Input
                    id="new_pin"
                    type="password"
                    inputMode="numeric"
                    maxLength={4}
                    pattern="[0-9]{4}"
                    placeholder="****"
                    value={pinForm.new_pin}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                      setPinForm(prev => ({ ...prev, new_pin: val }));
                    }}
                    data-testid="new-pin-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm_pin">PIN bestaetigen</Label>
                  <Input
                    id="confirm_pin"
                    type="password"
                    inputMode="numeric"
                    maxLength={4}
                    pattern="[0-9]{4}"
                    placeholder="****"
                    value={pinForm.confirm_pin}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                      setPinForm(prev => ({ ...prev, confirm_pin: val }));
                    }}
                    data-testid="confirm-pin-input"
                  />
                </div>
                {pinForm.new_pin && pinForm.confirm_pin && pinForm.new_pin !== pinForm.confirm_pin && (
                  <p className="text-sm text-destructive">PINs stimmen nicht ueberein</p>
                )}
                <Button
                  variant="outline"
                  className="rounded-full"
                  onClick={handleChangePin}
                  disabled={savingPin || !pinForm.new_pin || pinForm.new_pin.length !== 4 || pinForm.new_pin !== pinForm.confirm_pin}
                  data-testid="change-pin-btn"
                >
                  {savingPin ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  PIN aendern
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="border-border/60">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Kontoinformationen</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm text-muted-foreground">Rolle</p>
                  <p className="font-medium">
                    {user?.effective_permissions?.role_name || (user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : '-')}
                  </p>
                  {user?.effective_permissions?.role_name && user?.role && (
                    <p className="text-xs text-muted-foreground capitalize mt-0.5">
                      Ebene: {user.role}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Konto erstellt</p>
                  <p className="font-medium">
                    {user?.created_at ? new Date(user.created_at).toLocaleDateString('de-DE') : '-'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
